/**
 * Which language this tab is showing, and where that fact is kept.
 *
 * Deliberately free of React and of axios. `lib/api.ts` imports from here to
 * stamp `Accept-Language` on every request, and the provider imports from here
 * to render — so anything reaching the other way would close a cycle. It also
 * means the rules below can be tested without a DOM, which is how the rest of
 * this codebase tests its storage logic.
 */

export const LOCALES = ['bs', 'hr', 'sr', 'en'] as const

export type Locale = (typeof LOCALES)[number]

/**
 * The language everything falls back to.
 *
 * Bosnian, and **not** `navigator.language`, which is the tempting default and
 * the wrong one here. The shop-floor Android phones this runs on report `en-US`
 * whether or not anybody chose anything — it is what the ROM shipped with, not
 * a statement about the person holding it — so reading it would put a Bosnian
 * worker's first screen into English on the strength of a factory setting.
 *
 * The server makes the same judgement for the same reason; see SetLocale.
 */
export const DEFAULT_LOCALE: Locale = 'bs'

const STORAGE_KEY = 'kapro.locale'

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value)
}

/**
 * The language this tab is rendering in, held in memory.
 *
 * Read on every request by the axios interceptor, so it cannot be a storage
 * read each time — and it must not be, for the same reason `activeToken` in
 * lib/api.ts is not: `localStorage` is shared by every tab on the origin, and a
 * second person switching language in another tab should not silently repoint
 * this one mid-session.
 */
let activeLocale: Locale = DEFAULT_LOCALE

/**
 * The language somebody actually chose, or null if nobody has.
 *
 * The distinction carries real weight and is not a nicety. Null means "follow
 * whatever the server says", which is what lets a company-wide default reach a
 * device that has never been touched. A stored value means "this person
 * decided", and the server is told so on every request.
 */
export function getStoredLocale(): Locale | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)

    return isLocale(stored) ? stored : null
  } catch {
    /*
     * Private browsing and locked-down kiosk profiles can throw on access
     * rather than return null. A language preference is not worth a blank
     * screen, so an unreadable store simply means nobody has chosen.
     */
    return null
  }
}

export function getActiveLocale(): Locale {
  return activeLocale
}

/** Remember a deliberate choice, and use it from here on. */
export function setStoredLocale(locale: Locale): void {
  activeLocale = locale

  try {
    localStorage.setItem(STORAGE_KEY, locale)
  } catch {
    // As above: the choice still applies to this tab, it just will not survive
    // a reload. Better than refusing to switch at all.
  }
}

/**
 * Take the server's answer, but only where nobody has said otherwise.
 *
 * Every API response carries `Content-Language`, because the server resolves
 * from things the client cannot see — the account's saved preference and the
 * company default. A device signing in for the first time therefore learns the
 * right language from its first response instead of showing the fallback until
 * somebody notices.
 *
 * The guard is the whole point. Without it this would undo a choice the person
 * just made every time a response arrived, including the response to the very
 * request that saved it.
 */
export function adoptServerLocale(locale: string | undefined): void {
  if (getStoredLocale() !== null || !isLocale(locale)) return

  activeLocale = locale
}

/**
 * Tell the document which language it is in.
 *
 * `index.html` ships `lang="bs"`, which is right until somebody switches and
 * then silently wrong for the rest of the session. The attribute is not
 * decorative: a screen reader picks its pronunciation rules from it, so English
 * read with Bosnian phonemes is what a switch to English otherwise produces,
 * and Chrome offers to translate a page whose declared language is not the
 * reader's — which it was doing to Bosnian speakers reading Bosnian.
 *
 * Lives here rather than in the provider so it moves with the value it
 * describes, and is guarded for the no-DOM case because this module is
 * deliberately testable without one.
 */
export function syncDocumentLanguage(locale: Locale): void {
  if (typeof document === 'undefined') return

  document.documentElement.lang = locale
}

/**
 * Restore what this tab should open in, before the first render.
 *
 * Only a stored choice is available at this point — the server has not been
 * asked anything yet — so a device that has never chosen starts on the default
 * and moves once `adoptServerLocale` hears back.
 */
export function initLocale(): Locale {
  activeLocale = getStoredLocale() ?? DEFAULT_LOCALE

  return activeLocale
}
